import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ArrowRight, Calendar, FileSignature, Zap, Star, Puzzle, Workflow, Mic,
  Ticket, CheckSquare, Binoculars, Sparkles, Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/hero-card.webp";

export const Route = createFileRoute("/vi/platform/run-your-business")({
  head: () => ({
    meta: [
      { title: "Vận hành doanh nghiệp — banana.cards" },
      { name: "description", content: "Booking, cuộc họp, hợp đồng, review, pipeline, task, sự kiện và prospecting — mọi business app, trên Banana." },
      { property: "og:title", content: "Vận hành doanh nghiệp — banana.cards" },
      { property: "og:description", content: "Mọi business app, trên Banana." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/run-your-business", "vi"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Vận hành doanh nghiệp — banana.cards",
          description: "Booking, cuộc họp, hợp đồng, review, pipeline, task, sự kiện và prospecting — mọi business app, trên Banana.",
          url: "https://banana.cards/vi/platform/run-your-business",
          isPartOf: { "@type": "WebSite", name: "banana.cards", url: "https://banana.cards" },
        }),
      },
    ],
  }),
  component: Page,
});

type App = { title: string; tag: string; desc: string; href: string; icon: LucideIcon; bullets: string[]; stat?: { value: string; label: string }; };

const apps: App[] = [
  { title: "Booking", tag: "Lịch hẹn", desc: "Cho khách đặt thời gian 24/7. Định nghĩa dịch vụ, thời lượng, buffer, lịch và nhận thanh toán khi đặt.", href: "/vi/platform/bookings", icon: Calendar,
    bullets: ["Dịch vụ & giá tùy chỉnh", "Slot group, 1:1, lặp lại", "Thanh toán Stripe tại checkout", "Nhắc & xác nhận tự động"],
    stat: { value: "24/7", label: "Khách tự đặt" } },
  { title: "Hợp đồng & e-Sign", tag: "Giấy tờ", desc: "Xây template tái sử dụng, điền placeholder, gửi ký điện tử pháp lý và track mọi trạng thái.", href: "/vi/platform/contracts", icon: FileSignature,
    bullets: ["Template smart tái sử dụng", "Biến placeholder mỗi khách", "Audit trail & PDF đã ký", "Nhắc nhở doc chưa ký"],
    stat: { value: "e-Sign", label: "Pháp lý ràng buộc" } },
  { title: "Cuộc họp thông minh", tag: "AI notetaker", desc: "Tham gia cuộc gọi với AI notetaker ghi âm, transcript và tóm tắt — mọi quyết định và action item tự capture.", href: "/vi/platform/meetings", icon: Mic,
    bullets: ["Auto-join Zoom, Meet, Teams", "Transcript searchable", "Tóm tắt AI + action item", "Chia sẻ team 1-click"],
    stat: { value: "AI", label: "Ghi chú & tóm tắt" } },
  { title: "CRM", tag: "Liên hệ", desc: "CRM sạch, nhanh capture mọi tap, scan và lead — với trường tùy chỉnh, tag, ghi chú và timeline đầy đủ.", href: "/vi/platform/crm", icon: Sparkles,
    bullets: ["Auto-capture từ tap thẻ", "Trường & tag tùy chỉnh", "Timeline mỗi contact", "Bulk action & smart list"],
    stat: { value: "1 inbox", label: "Mọi lead, sắp xếp" } },
  { title: "Pipeline", tag: "Deal", desc: "Kanban deal stage với trường tùy chỉnh, yêu cầu stage và automation — đẩy công việc không chaos spreadsheet.", href: "/vi/platform/pipelines", icon: Workflow,
    bullets: ["Kanban kéo thả", "Yêu cầu stage & gate", "Trường deal tùy chỉnh", "Forecast & weighted value"],
    stat: { value: "Kanban", label: "Pipeline trực quan" } },
  { title: "Task", tag: "Hoàn thành", desc: "Gán, lên lịch và hoàn thành to-do cho bạn và team — link với contact, deal và cuộc họp.", href: "/vi/platform/tasks", icon: CheckSquare,
    bullets: ["Task cá nhân & team", "Due date & nhắc nhở", "Link với contact/deal", "Lặp lại & subtask"],
    stat: { value: "0", label: "Bóng rớt" } },
  { title: "Scout", tag: "AI prospecting", desc: "Tìm và qualify prospect từ Google Places bằng natural language. Mô tả khách hàng lý tưởng — Scout xây list.", href: "/vi/platform/scout", icon: Binoculars,
    bullets: ["Tìm bằng natural language", "Email, site & social", "Làm giàu & chấm điểm", "Export ra CRM"],
    stat: { value: "AI", label: "Prospect tự động" } },
  { title: "Bộ AI", tag: "AI có sẵn", desc: "Bộ công cụ AI đan trong mọi app — draft, tóm tắt, làm giàu, reply và gợi ý next best action.", href: "/vi/platform/ai-suite", icon: Zap,
    bullets: ["AI draft & reply", "Auto-enrichment", "Smart next action", "Hoạt động mọi app"],
    stat: { value: "10x", label: "Ops nhanh hơn" } },
];

const more = [
  { title: "Review", desc: "Gửi yêu cầu review và showcase social proof 5 sao trên thẻ.", href: "/vi/platform/reviews", icon: Star },
  { title: "Sự kiện & RSVP", desc: "Tạo trang sự kiện, quản lý RSVP và check-in tại cửa.", href: "/vi/platform/events", icon: Ticket },
  { title: "Tự động hóa", desc: "Trigger, action và tích hợp chạy trong khi bạn ngủ.", href: "/vi/platform/automations", icon: Zap },
  { title: "Tích hợp", desc: "Plug vào Google, Outlook, Zapier và 50+ công cụ.", href: "/vi/platform/integrations", icon: Puzzle },
];

function slug(s: string) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

function Page() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName="Vận hành doanh nghiệp" />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              Mọi business app, <em className="italic">cùng một nơi.</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
              Cài chỉ những gì cần. Booking, cuộc họp, hợp đồng, CRM, pipeline, task, prospecting và AI — một login, mọi thứ nói chuyện với nhau.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                Bắt đầu — miễn phí <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/vi/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                Xem giá
              </Link>
            </div>
            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[{ v: "8+", l: "App" }, { v: "1", l: "Login & hóa đơn" }, { v: "50+", l: "Tích hợp" }].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl">{s.v}</dt>
                  <dd className="mt-1 text-xs text-muted-foreground">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={heroImg} alt="Vận hành doanh nghiệp" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Có gì bên trong</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Mọi công cụ, <em className="italic">trong một liếc nhìn.</em>
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {apps.map((f) => {
              const Icon = f.icon;
              return (
                <Link key={f.href} to={f.href} className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc.split(".")[0]}.</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                    Tìm hiểu <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {apps.map((app, i) => (
        <DeepDive key={app.href} app={app} flip={i % 2 === 1} muted={i % 2 === 1} />
      ))}

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Còn nữa</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">Dàn diễn viên phụ.</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {more.map((m) => {
              const Icon = m.icon;
              return (
                <Link key={m.href} to={m.href} aria-label={`${m.title} — tìm hiểu thêm về tính năng này`} className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{m.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Một nền tảng. <em className="italic text-brand-lime">Mọi workflow.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Cài app cần hôm nay, bật phần còn lại khi phát triển.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Bắt đầu — miễn phí
              </CtaLink>
              <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Liên hệ sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function DeepDive({ app, flip, muted }: { app: App; flip: boolean; muted: boolean }) {
  const Icon = app.icon;
  const sectionBg = muted ? "bg-muted" : "bg-background";
  const boxBg = muted ? "bg-card" : "bg-muted";
  return (
    <section id={slug(app.title)} className={sectionBg}>
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
        <div className={flip ? "md:order-2" : ""}>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-foreground text-background">
              <Icon className="h-5 w-5" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{app.tag}</p>
          </div>
          <h2 className="mt-5 font-display text-3xl leading-[1.1] md:text-[2.5rem]">{app.title}</h2>
          <p className="mt-4 max-w-lg text-base text-muted-foreground md:text-lg">{app.desc}</p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 max-w-lg">
            {app.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={app.href} className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
              Khám phá {app.title} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className={flip ? "md:order-1" : ""}>
          <div className={`rounded-3xl ${boxBg} p-8 md:p-10`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{app.tag}</span>
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
            {app.stat && (
              <div className="mt-6">
                <div className="font-display text-5xl md:text-7xl leading-none">{app.stat.value}</div>
                <div className="mt-3 text-sm text-muted-foreground">{app.stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}